import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { RealEstateImage } from '@immosky/types';
import { clamp } from '@immosky/shared';

interface Dot {
  index: number;
  isHidden: boolean;
  isSmall: boolean;
  isTiny: boolean;
}

@Component({
  selector: 'lib-list-card-dots',
  templateUrl: './list-card-dots.component.html',
  styleUrls: ['./list-card-dots.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListCardDotsComponent implements OnChanges {
  private readonly DISPLAYED_DOTS_COUNT = 16;
  private overflowingCount = 0;
  private startOffset = 0;
  private startIndex = 0;
  private cutoffDotsLeftCount = 0;
  private endIndex = 0;
  private cutoffDotsRightCount = 0;

  dots: Dot[] = [];

  @Input()
  images: RealEstateImage[] = [];

  @Input()
  currentIndex = 0;

  @Output()
  dotClick = new EventEmitter<number>();

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.images) {
      this.dots = this.images.map((image, i) => ({
        index: i,
        isHidden: true,
        isSmall: false,
        isTiny: false,
      }));

      this.overflowingCount = Math.max(
        0,
        this.images.length - this.DISPLAYED_DOTS_COUNT
      );
    }

    if (changes.currentIndex) {
      this.update();
    }
  }

  private update() {
    this.determineVisibleRange();

    for (const dot of this.dots) {
      this.updateDot(dot);
    }

    this.cdRef.markForCheck();
  }

  private determineVisibleRange() {
    this.startOffset = Math.min(2, this.maxIndex - this.currentIndex);

    this.startIndex = Math.abs(
      Math.min(
        this.DISPLAYED_DOTS_COUNT - this.currentIndex - this.startOffset,
        0
      )
    );
    this.startIndex = clamp(
      0,
      this.startIndex,
      this.dotCount - this.DISPLAYED_DOTS_COUNT
    );
    this.cutoffDotsLeftCount = this.startIndex;

    this.endIndex = this.startIndex + this.DISPLAYED_DOTS_COUNT;
    this.endIndex = clamp(0, this.endIndex, this.dotCount);
    this.cutoffDotsRightCount = this.maxIndex - this.endIndex;
  }

  private updateDot(dot: Dot) {
    dot.isHidden = dot.index < this.startIndex || dot.index > this.endIndex;

    if (dot.isHidden) {
      return;
    }

    const isAtLeftEdge = dot.index === this.startIndex && this.startIndex > 0;
    const isNearLeftEdge =
      dot.index === this.startIndex + 1 && this.startIndex > 0;

    const isAtRightEdge = dot.index === this.endIndex;
    const isNearRightEdge =
      dot.index === this.endIndex - 1 && this.endIndex !== this.dotCount;

    dot.isTiny =
      (isAtLeftEdge && this.cutoffDotsLeftCount >= 2) ||
      (isAtRightEdge && this.cutoffDotsRightCount >= 2);
    dot.isSmall =
      (isAtLeftEdge && this.cutoffDotsLeftCount === 1) ||
      (isNearLeftEdge && this.cutoffDotsLeftCount >= 2) ||
      (isAtRightEdge && this.cutoffDotsRightCount !== 0) ||
      (isNearRightEdge && this.cutoffDotsRightCount >= 2);
  }

  goToImage(i: number) {
    this.dotClick.emit(i);
  }

  trackByFn(i: number, dot: Dot): number {
    return dot.index;
  }

  get dotCount(): number {
    return this.dots.length;
  }

  get maxIndex(): number {
    return this.dots.length - 1;
  }

  get transformX(): string {
    const translateStartIndex = this.DISPLAYED_DOTS_COUNT - 2;
    const offsetCount = this.currentIndex - translateStartIndex;
    const translateEndIndex = this.maxIndex - translateStartIndex - 2;

    const x = clamp(0, offsetCount * 100, translateEndIndex * 100);
    return `translateX(-${x}%)`;
  }

  get isBasic(): boolean {
    return this.dots.length <= this.DISPLAYED_DOTS_COUNT;
  }
}
